import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Box, Menu, Typography, Button, TextField, InputAdornment, Switch, } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import CustomLoader from "./CustomLoader";
const VisibleClientsDropdown = ({ clients, onChange, currentDateRange, label = "Visible Client", showIcon = true, onItemToggle, itemName = "clients", enableUpdateButton = true, iconSrc = '/assets/visibleClients.svg', buttonWidth = 185, primaryColor = "#ec4899", onToggle, onBulkToggle, adjustWidth = 210 }) => {
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [localClients, setLocalClients] = useState(clients);
    const [loadingClientId, setLoadingClientId] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectAll, setSelectAll] = useState(true);
    const isProcessingRef = useRef(false);
    const pendingUpdatesRef = useRef([]);
    useEffect(() => {
        setLocalClients(clients); // ⭐ Always trust server state
        setIsDirty(false);
    }, [clients]);
    const open = Boolean(anchorEl);
    useEffect(() => {
        setLocalClients(clients);
        setIsDirty(false);
    }, [clients]);
    // Filter clients based on search query
    const filteredClients = localClients
        .filter(client => client.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));
    const handleSelectAllToggle = async () => {
        const next = !selectAll;
        setSelectAll(next);
        const updatedClients = localClients.map(c => ({
            ...c,
            visible: next
        }));
        setLocalClients(updatedClients);
        // ⭐ APPLY MODE → ONLY LOCAL CHANGE
        if (enableUpdateButton) {
            setIsDirty(true);
            return; // ⭐ VERY IMPORTANT
        }
        // ⭐ INSTANT MODE → API CALL
        setLoadingClientId("select_all");
        try {
            const targetIds = next
                ? localClients.filter(c => !c.visible).map(c => c.id)
                : localClients.filter(c => c.visible).map(c => c.id);
            if (targetIds.length && onBulkToggle) {
                await onBulkToggle(targetIds, next);
            }
            onChange(updatedClients);
        }
        catch (err) {
            console.error("Select All error:", err);
            setLocalClients(clients);
            setSelectAll(!next);
        }
        finally {
            setLoadingClientId(null);
        }
    };
    const handleToggle = async (clientId, currentVisibility) => {
        if (loadingClientId || pendingUpdatesRef.current.includes(clientId))
            return;
        const newVisible = !currentVisibility;
        const updatedClients = localClients.map(c => c.id === clientId ? { ...c, visible: newVisible } : c);
        setLocalClients(updatedClients);
        // ⭐ APPLY MODE → Only local update
        if (enableUpdateButton) {
            setIsDirty(true);
            return;
        }
        // ⭐ INSTANT MODE → API now
        setLoadingClientId(clientId);
        try {
            if (onToggle) {
                await onToggle(clientId, newVisible);
            }
            onChange(updatedClients);
        }
        catch {
            setLocalClients(localClients);
        }
        finally {
            setLoadingClientId(null);
        }
    };
    const handleBulkUpdate = async () => {
        setLoadingClientId("bulk");
        try {
            const changedClients = localClients.filter((lc, idx) => lc.visible !== clients[idx]?.visible);
            const toHide = changedClients.filter(c => !c.visible).map(c => c.id);
            const toUnhide = changedClients.filter(c => c.visible).map(c => c.id);
            if (toHide.length && onBulkToggle) {
                await onBulkToggle(toHide, false);
            }
            if (toUnhide.length && onBulkToggle) {
                await onBulkToggle(toUnhide, true);
            }
            onChange(localClients);
            setIsDirty(false);
            setAnchorEl(null);
        }
        finally {
            setLoadingClientId(null);
        }
    };
    const handleClose = () => {
        setAnchorEl(null);
        setSearchQuery(""); // Clear search on close
        if (isDirty) {
            setLocalClients(clients);
            setIsDirty(false);
        }
    };
    const visibleCount = localClients.filter(c => c.visible).length;
    const totalCount = localClients.length;
    const buttonWidthDynamic = visibleCount === totalCount ? buttonWidth : adjustWidth;
    useEffect(() => {
        // Only update if clients actually changed
        if (JSON.stringify(clients) !== JSON.stringify(localClients)) {
            setLocalClients(clients);
            setIsDirty(false);
        }
    }, [clients]); // Remove localClients from dependency
    useEffect(() => {
        const allVisible = localClients.length > 0 && localClients.every(c => c.visible);
        setSelectAll(allVisible);
    }, [localClients]);
    const smallSwitch = {
        width: 36,
        height: 20,
        padding: 0,
        "& .MuiSwitch-switchBase": {
            padding: "2px",
            "&.Mui-checked": {
                transform: "translateX(16px)",
                color: "#fff",
                "& + .MuiSwitch-track": { bgcolor: primaryColor, opacity: 1 },
            },
        },
        "& .MuiSwitch-thumb": { width: 16, height: 16 },
        "& .MuiSwitch-track": { borderRadius: 10, bgcolor: "#D0D5DD", opacity: 1 },
    };
    return (_jsxs(Box, { children: [_jsxs(Box, { onClick: (e) => setAnchorEl(e.currentTarget), sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    border: `1px solid ${open ? primaryColor : "#D0D5DD"}`,
                    borderRadius: "8px",
                    px: 1.5,
                    cursor: "pointer",
                    height: "36px",
                    width: { xs: open ? 150 : 145, sm: buttonWidthDynamic },
                    bgcolor: "#fff",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    boxShadow: open ? `0 0 0 3px ${primaryColor}22` : "none",
                    "&:hover": { borderColor: open ? primaryColor : "#98A2B3" },
                }, children: [showIcon && (_jsx("img", { src: iconSrc, alt: "icon", style: { width: 16, height: 16, objectFit: "contain", flexShrink: 0 } })), _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '13px !important', fontWeight: 600, color: "#101828", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }, children: label }), _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '13px !important', fontWeight: 500, color: "#667085", flexShrink: 0 }, children: visibleCount === totalCount ? "All" : `${visibleCount}/${totalCount}` }), open
                        ? _jsx(KeyboardArrowUpIcon, { sx: { fontSize: 18, color: primaryColor, flexShrink: 0 } })
                        : _jsx(KeyboardArrowDownIcon, { sx: { fontSize: 18, color: "#667085", flexShrink: 0 } })] }), _jsxs(Menu, { anchorEl: anchorEl, open: open, onClose: handleClose, disableAutoFocusItem: true, PaperProps: {
                    sx: {
                        width: { xs: 150, sm: 280 },
                        maxHeight: "400px !important",
                        borderRadius: "8px",
                        boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
                        border: "1px solid #EAECF0",
                        mt: 0.75,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        "@media (max-width: 600px)": {
                            left: "auto !important",
                            right: "15px !important",
                        },
                    },
                }, MenuListProps: {
                    sx: { py: 0, display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", maxHeight: "none !important" }
                }, children: [_jsx(Box, { sx: { px: 2, pt: 1.5, pb: 1, flexShrink: 0 }, children: _jsxs(Typography, { sx: { fontSize: 12, fontWeight: 600, color: "#667085", textTransform: "uppercase", letterSpacing: "0.5px" }, children: [visibleCount, " of ", totalCount, " ", itemName, " visible"] }) }), _jsx(Box, { sx: { px: 1.5, pb: 1, flexShrink: 0 }, children: _jsx(TextField, { fullWidth: true, size: "small", placeholder: `Search ${itemName}...`, value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), autoFocus: true, InputProps: {
                                startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(SearchIcon, { sx: { fontSize: 16, color: "#98A2B3" } }) })),
                                endAdornment: searchQuery && (_jsx(InputAdornment, { position: "end", children: _jsx(CloseIcon, { sx: { fontSize: 16, color: "#98A2B3", cursor: "pointer", "&:hover": { color: "#667085" } }, onClick: () => setSearchQuery("") }) })),
                            }, sx: {
                                "& .MuiOutlinedInput-root": {
                                    fontSize: 13,
                                    borderRadius: "8px",
                                    bgcolor: "#F9FAFB",
                                    "& fieldset": { borderColor: "#EAECF0" },
                                    "&:hover fieldset": { borderColor: "#D0D5DD" },
                                    "&.Mui-focused fieldset": { borderColor: primaryColor, borderWidth: "1px" },
                                },
                            } }) }), _jsx(Box, { sx: { mx: 1.5, mb: 0.5, borderBottom: "1px solid #F2F4F7" } }), _jsxs(Box, { onClick: handleSelectAllToggle, sx: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1,
                            mx: 0.5,
                            borderRadius: "8px",
                            cursor: "pointer",
                            flexShrink: 0,
                            "&:hover": { bgcolor: "#F9FAFB" },
                        }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '13px !important', fontWeight: 600, color: "#344054" }, children: selectAll ? "Disable All" : "Enable All" }), _jsx(Switch, { checked: selectAll, size: "small", sx: { ...smallSwitch, pointerEvents: "none", flexShrink: 0 }, onClick: (e) => e.stopPropagation(), onChange: handleSelectAllToggle })] }), _jsx(Box, { sx: { mx: 1.5, mt: 0.5, mb: 0.5, borderBottom: "1px solid #F2F4F7" } }), _jsx(Box, { sx: {
                            flex: 1,
                            overflowY: "auto",
                            overflowX: "hidden",
                            px: 0.5,
                            pb: 0.5,
                            "&::-webkit-scrollbar": { width: "4px" },
                            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                            "&::-webkit-scrollbar-thumb": { bgcolor: "#E4E7EC", borderRadius: "4px", "&:hover": { bgcolor: "#D0D5DD" } },
                        }, children: filteredClients.length === 0 ? (_jsx(Box, { sx: { textAlign: "center", py: 3 }, children: _jsxs(Typography, { sx: { fontSize: 13, color: "#98A2B3" }, children: ["No ", itemName, " found"] }) })) : (filteredClients.map((client) => (_jsxs(Box, { onClick: () => !loadingClientId && handleToggle(client.id, client.visible), sx: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                px: 1.5,
                                py: 0.875,
                                borderRadius: "8px",
                                cursor: loadingClientId ? "not-allowed" : "pointer",
                                "&:hover": { bgcolor: "#F9FAFB" },
                            }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '13px !important', fontWeight: 500, color: "#344054", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, textAlign: "left", mr: 1 }, children: client.name }), _jsx(Box, { sx: { width: 36, height: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }, children: loadingClientId === client.id ? (_jsx(CustomLoader, { size: 16, sx: { color: primaryColor } })) : (_jsx(Switch, { checked: client.visible, size: "small", sx: { ...smallSwitch, pointerEvents: "none" } })) })] }, client.id)))) }), (enableUpdateButton && isDirty) && (_jsx(Box, { sx: { p: 1.5, flexShrink: 0, borderTop: "1px solid #EAECF0" }, children: _jsx(Button, { fullWidth: true, variant: "contained", onClick: handleBulkUpdate, disabled: !isDirty || loadingClientId === "bulk", sx: {
                                bgcolor: primaryColor,
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: 13,
                                py: 0.875,
                                borderRadius: "8px",
                                textTransform: "none",
                                boxShadow: "none",
                                "&:hover": { bgcolor: primaryColor, boxShadow: "0 4px 8px rgba(0,0,0,0.12)" },
                                "&.Mui-disabled": { bgcolor: "#E4E7EC", color: "#98A2B3" },
                            }, children: loadingClientId === "bulk" ? _jsx(CustomLoader, { size: 18, color: "inherit" }) : "Apply" }) }))] })] }));
};
export default VisibleClientsDropdown;
